import { useAuth } from '@contexts/User.context';
import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';

/**
 * Whether the signed-in user may annotate and train, by tier and access. Each
 * refusal is explained in a toast. With a `task` (Model view) the trial checks
 * that are about the model apply too; object annotation passes none.
 *
 * @returns {(options?: { task?: object }) => boolean}
 */
export const useTrainingPermission = () => {
	const { addToast } = useToast();
	const { mapObject } = useProject();
	const {
		tierTrial,
		trialEnded,
		tierPremium,
		tierPro,
		subscriptionActive,
		isEnterprise,
		activeOrgAccess,
		userAccess,
	} = useAuth();

	return ({ task } = {}) => {
		const refuse = message => {
			addToast({ id: 'training-permission-denied', bg: 'warning', title: message });
			return false;
		};

		if (tierTrial) {
			if (trialEnded) return refuse('Your trial has ended and you can no longer annotate');
			if (task?.multimodel) return refuse('You cannot train a multimodel as a trial user');
			if (task?.is_trained) return refuse('You cannot improve accuracy as a trial user');

			const layers = mapObject?.getLayers()?.getArray();
			const trainingDataLayerExists = layers?.some(
				layer => layer.get('name') === 'Training data'
			);
			if (trainingDataLayerExists)
				return refuse(
					'Training data exists. You can only train a model once as a trial user.'
				);
		}

		if (tierPremium && !userAccess?.includes('train'))
			return refuse('Your user does not have access rights to train AI');

		if (tierPro && !subscriptionActive)
			return refuse('Your user needs an active subscription to train AI');

		if (isEnterprise) {
			const hasOrgAccess =
				activeOrgAccess.includes('all') || activeOrgAccess.includes('train');
			if (!hasOrgAccess) return refuse('Your user does not have access rights to train AI');
		}

		return true;
	};
};
